/**
 * The BOUND gateway-HMAC bundle (D644) — freshness + audience + request
 * binding layered ON TOP of the D192/D353/D355 principal bundle.
 *
 * ## Why this exists
 *
 * The principal bundle in `./index` proves WHO the request is (14 signed
 * principal/operational headers). It carries no freshness, no audience, and no
 * request binding, so a captured bundle replays fleet-wide until the shared
 * secret rotates (open drift `14b5f78f`; the auth#298 panel independently found
 * `GATEWAY_BUNDLE_ORDER` has no timestamp/nonce/expiry and the boundary sets
 * `iat:0, exp:0`). D644 (locked, STRICTER posture, operator-delegated) fixes
 * that with a second, bound signature over:
 *
 *   `jti` (single-use nonce) · `iat`+`nbf`+`exp` (short TTL, skew-bounded) ·
 *   `aud` (the specific target service) · `method | path | sha256(body)`
 *   (request binding) · and the principal bundle's own signature (so the
 *   freshness/binding claims are cryptographically tied to THIS principal set —
 *   an attacker cannot splice a fresh bound header onto a different bundle).
 *
 * ## Why it is ADDITIVE and inert until an adopter opts in
 *
 * `GATEWAY_BUNDLE_ORDER` and the principal `sign/verifyGatewayBundle` are a
 * byte-for-byte agreement between the edge signer and every verifier; changing
 * them is a fleet-coordinated re-lock, not a library change (see `./index`).
 * So this layer adds NOTHING to that canonical. It is a NEW signature over a
 * NEW claim set, carried in NEW headers ({@link GATEWAY_BOUND_HEADER} +
 * {@link GATEWAY_BOUND_SIGNATURE_HEADER}). A verifier that does not call
 * {@link verifyBoundGatewayBundle} is unaffected; the edge does not stamp the
 * bound headers until it opts in. Shipping this library changes zero runtime
 * behaviour until the coordinated adopter rollout (D644 § rollout discipline).
 *
 * ## Crypto reuse
 *
 * The bound signature is computed by the SAME audited primitive as the
 * principal bundle — {@link signGatewayBundle}/{@link verifyGatewayBundle} — by
 * canonicalizing the bound claims under a bound-specific order
 * ({@link BOUND_CLAIM_ORDER}). That inherits, verbatim, the strict
 * unpadded-base64url handling, constant-time compare, and dual-verify-within-
 * rotation-grace (D356). No crypto is re-implemented here.
 *
 * Refs: D644 (this), D192/D353/D355 (principal bundle), D356 (rotation),
 * drift `14b5f78f`.
 */

import { createHash } from "node:crypto";
import {
  GatewayBundleIncompleteError,
  type GatewayHmacSecrets,
  signGatewayBundle,
  verifyGatewayBundle,
} from "./index";
import type { GatewayBundleReplayStore } from "./replay-store";

/** Wire header carrying the bound claims (base64url(JSON)). */
export const GATEWAY_BOUND_HEADER = "x-nodii-gateway-bound";
/** Wire header carrying the bound signature (unpadded base64url HMAC). */
export const GATEWAY_BOUND_SIGNATURE_HEADER = "x-nodii-gateway-bound-signature";

/**
 * Default bound-bundle TTL: 60s (D644 § 2 target, tuned to real gateway→service
 * latency). Deliberately NOT `exp:0`.
 */
export const GATEWAY_BOUND_DEFAULT_TTL_MS = 60_000;

/**
 * Maximum accepted bound-bundle TTL (`exp - iat`). A signer that mints a
 * longer-lived bundle is rejected — the whole point is a tight window, so an
 * over-long `exp` (a mis-configured or hostile signer) must not widen the
 * replay surface. Default 5 min.
 */
export const GATEWAY_BOUND_MAX_TTL_MS = 5 * 60_000;

/**
 * Clock-skew allowance for `nbf`/`exp` (D644 § 3). Applied symmetrically: a
 * bundle is valid for `[nbf - skew, exp + skew]`. Default 5s.
 */
export const GATEWAY_BOUND_DEFAULT_SKEW_MS = 5_000;

/**
 * The canonical order of bound claims. Passed as the `order` to the principal
 * bundle's canonicalizer so the SAME HMAC primitive signs/verifies these. These
 * are internal canonicalization keys, NOT wire header names (the whole claim set
 * travels in one {@link GATEWAY_BOUND_HEADER}). Changing this order changes the
 * bound MAC — a coordinated re-lock like {@link GATEWAY_BUNDLE_ORDER}, but the
 * bound layer only has one signer and one verifier per request so it is bound to
 * this library version, not hand-copied across repos.
 */
export const BOUND_CLAIM_ORDER: readonly string[] = [
  "gwb-jti",
  "gwb-iat",
  "gwb-nbf",
  "gwb-exp",
  "gwb-aud",
  "gwb-method",
  "gwb-path",
  "gwb-body-sha256",
  "gwb-bundle-sig",
] as const;

/** The bound claim set (D644 § 1–5). All times are UNIX milliseconds. */
export interface GatewayBoundClaims {
  /** Single-use nonce (D644 § 1). */
  jti: string;
  /** Issued-at (ms). */
  iat: number;
  /** Not-before (ms). */
  nbf: number;
  /** Expiry (ms). */
  exp: number;
  /** Target service id (D644 § 4). */
  aud: string;
  /** Bound HTTP method, upper-cased (D644 § 5). */
  method: string;
  /** Bound request path (D644 § 5). */
  path: string;
  /** `base64url(sha256(body))` (D644 § 5). */
  bodySha256: string;
  /**
   * The principal bundle's signature ({@link GATEWAY_HMAC_HEADER} value) — binds
   * these claims to THAT principal set so a fresh bound header cannot be spliced
   * onto a different bundle.
   */
  bundleSig: string;
}

/** `base64url(sha256(bytes))`, unpadded — the body-binding digest (D644 § 5). */
export function sha256Base64Url(body: Uint8Array): string {
  return createHash("sha256")
    .update(Buffer.from(body))
    .digest("base64url")
    .replace(/=+$/, "");
}

/** Build the `headers` dict the principal canonicalizer consumes from claims. */
function claimsToCanonicalHeaders(
  claims: GatewayBoundClaims,
): Record<string, string> {
  return {
    "gwb-jti": claims.jti,
    "gwb-iat": String(claims.iat),
    "gwb-nbf": String(claims.nbf),
    "gwb-exp": String(claims.exp),
    "gwb-aud": claims.aud,
    "gwb-method": claims.method,
    "gwb-path": claims.path,
    "gwb-body-sha256": claims.bodySha256,
    "gwb-bundle-sig": claims.bundleSig,
  };
}

/** Encode claims for the wire: base64url(JSON), unpadded. */
export function encodeBoundClaims(claims: GatewayBoundClaims): string {
  return Buffer.from(JSON.stringify(claims), "utf8")
    .toString("base64url")
    .replace(/=+$/, "");
}

/**
 * Strictly decode + validate the wire claim blob. Returns `null` for anything
 * that is not a well-formed claim object with the exact field types (never
 * throws on attacker input).
 */
export function decodeBoundClaims(blob: string): GatewayBoundClaims | null {
  if (typeof blob !== "string" || blob.length === 0) return null;
  if (!/^[A-Za-z0-9_-]*$/.test(blob)) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(Buffer.from(blob, "base64url").toString("utf8"));
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const o = parsed as Record<string, unknown>;
  const isFiniteInt = (v: unknown): v is number =>
    typeof v === "number" && Number.isFinite(v) && Number.isInteger(v);
  if (
    typeof o.jti !== "string" ||
    o.jti.length === 0 ||
    !isFiniteInt(o.iat) ||
    !isFiniteInt(o.nbf) ||
    !isFiniteInt(o.exp) ||
    typeof o.aud !== "string" ||
    o.aud.length === 0 ||
    typeof o.method !== "string" ||
    o.method.length === 0 ||
    typeof o.path !== "string" ||
    typeof o.bodySha256 !== "string" ||
    o.bodySha256.length === 0 ||
    typeof o.bundleSig !== "string" ||
    o.bundleSig.length === 0
  ) {
    return null;
  }
  return {
    jti: o.jti,
    iat: o.iat,
    nbf: o.nbf,
    exp: o.exp,
    aud: o.aud,
    method: o.method,
    path: o.path,
    bodySha256: o.bodySha256,
    bundleSig: o.bundleSig,
  };
}

export interface SignBoundGatewayBundleOptions {
  /** Target service id → `aud`. */
  audience: string;
  /** HTTP method (case-normalized to upper). */
  method: string;
  /** Request path. */
  path: string;
  /** Raw request body bytes (empty for no-body requests). */
  body: Uint8Array;
  /** The principal bundle signature ({@link GATEWAY_HMAC_HEADER} value). */
  bundleSignature: string;
  /** Raw signing secret bytes (edge signs with `current`). */
  secret: Uint8Array;
  /** Unique nonce for this bundle. MUST be unpredictable (crypto-random). */
  jti: string;
  /** Issued-at (ms). Injectable for tests; defaults to now at call time. */
  nowMs: number;
  /** Validity window (ms). Default {@link GATEWAY_BOUND_DEFAULT_TTL_MS}. */
  ttlMs?: number;
}

export interface SignedBoundGatewayBundle {
  /** Value for {@link GATEWAY_BOUND_HEADER}. */
  boundHeader: string;
  /** Value for {@link GATEWAY_BOUND_SIGNATURE_HEADER}. */
  boundSignature: string;
  /** The claims that were signed (for logging/observability). */
  claims: GatewayBoundClaims;
}

/**
 * Sign a bound bundle (edge side). Produces the two wire header values the edge
 * stamps alongside the principal bundle. Does NOT mint the `jti` or read the
 * clock implicitly — both are passed in so signing is deterministic and
 * testable; the caller supplies a crypto-random `jti` and `nowMs`.
 */
export function signBoundGatewayBundle(
  opts: SignBoundGatewayBundleOptions,
): SignedBoundGatewayBundle {
  const ttlMs = opts.ttlMs ?? GATEWAY_BOUND_DEFAULT_TTL_MS;
  const claims: GatewayBoundClaims = {
    jti: opts.jti,
    iat: opts.nowMs,
    nbf: opts.nowMs,
    exp: opts.nowMs + ttlMs,
    aud: opts.audience,
    method: opts.method.toUpperCase(),
    path: opts.path,
    bodySha256: sha256Base64Url(opts.body),
    bundleSig: opts.bundleSignature,
  };
  const boundSignature = signGatewayBundle({
    headers: claimsToCanonicalHeaders(claims),
    order: BOUND_CLAIM_ORDER,
    secret: opts.secret,
  });
  return {
    boundHeader: encodeBoundClaims(claims),
    boundSignature,
    claims,
  };
}

export type VerifyBoundGatewayBundleReason =
  | "BOUND_MISSING"
  | "BOUND_MALFORMED"
  | "BOUND_SIG_MISMATCH"
  | "BOUND_SIG_BAD_BASE64URL"
  | "OUT_OF_ROTATION_GRACE"
  | "WRONG_AUDIENCE"
  | "NOT_YET_VALID"
  | "EXPIRED"
  | "TTL_TOO_LONG"
  | "BAD_TIME_WINDOW"
  | "METHOD_MISMATCH"
  | "PATH_MISMATCH"
  | "BODY_MISMATCH"
  | "BUNDLE_SIG_MISMATCH"
  | "REPLAYED"
  | "REPLAY_STORE_ERROR";

export type VerifyBoundGatewayBundleResult =
  | { ok: true; used: "current" | "previous"; claims: GatewayBoundClaims }
  | { ok: false; reason: VerifyBoundGatewayBundleReason };

export interface VerifyBoundGatewayBundleOptions {
  /** Value received in {@link GATEWAY_BOUND_HEADER}. */
  boundHeader: string | undefined;
  /** Value received in {@link GATEWAY_BOUND_SIGNATURE_HEADER}. */
  boundSignature: string | undefined;
  /**
   * The principal bundle signature this service ALREADY verified
   * ({@link GATEWAY_HMAC_HEADER} value). The bound claims must be bound to
   * exactly this value, or the freshness/binding was minted for a different
   * principal set.
   */
  principalBundleSignature: string | undefined;
  /** This service's own id — must equal the bound `aud` (D644 § 4). */
  expectedAudience: string;
  /** Actual request method (case-insensitive compare). */
  method: string;
  /** Actual request path (exact compare). */
  path: string;
  /** Actual request body bytes. */
  body: Uint8Array;
  /** HMAC secrets (current + previous within rotation grace), as principal verify. */
  secrets: GatewayHmacSecrets;
  /** Single-use `jti` store (REQUIRED — no default; R1). */
  replayStore: GatewayBundleReplayStore;
  /** `now` in ms. Injectable for tests; defaults to `Date.now()`. */
  nowMs?: number;
  /** Clock-skew allowance (ms). Default {@link GATEWAY_BOUND_DEFAULT_SKEW_MS}. */
  skewMs?: number;
  /** Max accepted TTL (ms). Default {@link GATEWAY_BOUND_MAX_TTL_MS}. */
  maxTtlMs?: number;
  /** Rotation grace (ms) forwarded to principal verify. Default 24h. */
  rotationGraceMs?: number;
}

/**
 * Verify a bound bundle (service side). Order of checks is deliberate: cheap,
 * non-crypto structural + signature checks first; the `jti` reserve LAST, so a
 * malformed/expired/mis-audienced/replayed-body bundle never consumes a nonce
 * slot (a reserve is a write). Every failure is returned, never thrown.
 *
 * The caller MUST have already verified the PRINCIPAL bundle
 * (`verifyGatewayBundle`) and pass its signature as `principalBundleSignature`;
 * this layer binds to it but does not re-verify it.
 */
export async function verifyBoundGatewayBundle(
  opts: VerifyBoundGatewayBundleOptions,
): Promise<VerifyBoundGatewayBundleResult> {
  const now = opts.nowMs ?? Date.now();
  const skew = opts.skewMs ?? GATEWAY_BOUND_DEFAULT_SKEW_MS;
  const maxTtl = opts.maxTtlMs ?? GATEWAY_BOUND_MAX_TTL_MS;

  if (
    typeof opts.boundHeader !== "string" ||
    opts.boundHeader.length === 0 ||
    typeof opts.boundSignature !== "string" ||
    opts.boundSignature.length === 0
  ) {
    return { ok: false, reason: "BOUND_MISSING" };
  }

  const claims = decodeBoundClaims(opts.boundHeader);
  if (claims === null) {
    return { ok: false, reason: "BOUND_MALFORMED" };
  }

  // Verify the bound signature (reuses the audited HMAC primitive + dual-verify
  // within rotation grace) BEFORE trusting any claim value.
  let sigResult: ReturnType<typeof verifyGatewayBundle>;
  try {
    sigResult = verifyGatewayBundle({
      headers: claimsToCanonicalHeaders(claims),
      order: BOUND_CLAIM_ORDER,
      signature: opts.boundSignature,
      secrets: opts.secrets,
      rotationGraceMs: opts.rotationGraceMs,
    });
  } catch (e) {
    // canonicalize only throws GatewayBundleIncompleteError; claimsToCanonical
    // always supplies every key, so this is unreachable, but stay non-throwing.
    if (e instanceof GatewayBundleIncompleteError) {
      return { ok: false, reason: "BOUND_MALFORMED" };
    }
    throw e;
  }
  if (!sigResult.ok) {
    switch (sigResult.reason) {
      case "BAD_BASE64URL":
        return { ok: false, reason: "BOUND_SIG_BAD_BASE64URL" };
      case "OUT_OF_ROTATION_GRACE":
        return { ok: false, reason: "OUT_OF_ROTATION_GRACE" };
      case "INCOMPLETE_BUNDLE":
        return { ok: false, reason: "BOUND_MALFORMED" };
      default:
        return { ok: false, reason: "BOUND_SIG_MISMATCH" };
    }
  }

  // Bind to the principal set (D644): the claims must carry THIS request's
  // already-verified principal signature. Constant-time not required — this is
  // comparing two values the caller holds, not a secret.
  if (
    typeof opts.principalBundleSignature !== "string" ||
    opts.principalBundleSignature.length === 0 ||
    claims.bundleSig !== opts.principalBundleSignature
  ) {
    return { ok: false, reason: "BUNDLE_SIG_MISMATCH" };
  }

  // Audience (D644 § 4).
  if (claims.aud !== opts.expectedAudience) {
    return { ok: false, reason: "WRONG_AUDIENCE" };
  }

  // Time window (D644 § 2/§ 3). Reject an inverted or over-long window first.
  if (!(claims.exp > claims.iat) || !(claims.nbf >= claims.iat)) {
    return { ok: false, reason: "BAD_TIME_WINDOW" };
  }
  if (claims.exp - claims.iat > maxTtl) {
    return { ok: false, reason: "TTL_TOO_LONG" };
  }
  if (now + skew < claims.nbf) {
    return { ok: false, reason: "NOT_YET_VALID" };
  }
  if (now - skew > claims.exp) {
    return { ok: false, reason: "EXPIRED" };
  }

  // Request binding (D644 § 5).
  if (claims.method !== opts.method.toUpperCase()) {
    return { ok: false, reason: "METHOD_MISMATCH" };
  }
  if (claims.path !== opts.path) {
    return { ok: false, reason: "PATH_MISMATCH" };
  }
  if (claims.bodySha256 !== sha256Base64Url(opts.body)) {
    return { ok: false, reason: "BODY_MISMATCH" };
  }

  // Single-use nonce reserve LAST (D644 § 1) — the only write, and only after
  // everything else passed. TTL = remaining validity + skew, so the store holds
  // the jti exactly as long as a replay could still verify.
  const ttlMs = claims.exp + skew - now;
  let fresh: boolean;
  try {
    fresh = await opts.replayStore.reserve(claims.jti, ttlMs);
  } catch {
    // A store outage must FAIL CLOSED — we cannot prove this jti is unseen.
    return { ok: false, reason: "REPLAY_STORE_ERROR" };
  }
  if (!fresh) {
    return { ok: false, reason: "REPLAYED" };
  }

  return { ok: true, used: sigResult.used, claims };
}
